import React, { useState, useRef } from "react";
import { Row, Col, Form, Input, Button, Checkbox } from "antd";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import jwt_decode from "jwt-decode";
import { useDispatch } from "react-redux";
import { signupAction, googleLoginAction } from "@redux/actions/authActions";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";
import LeftContent from "./leftContent";
import { ErrorNotification } from "@components";
import { Helmet } from "react-helmet-async";
import ReCAPTCHA from "react-google-recaptcha";
import { verifyCaptcha } from "@api/auth/auth";

const initialFormData = {
  name: "",
  email: "",
  password: "",
};

const Signup = ({ history }) => {
  const dispatch = useDispatch();
  const captchaRef = useRef(null);
  const [formData, setFormData] = useState(initialFormData);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaSuccess, setCaptchaSuccess] = useState(false);

  // Handle form change
  const handleFormChange = (e) => {
    e.preventDefault();
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle custom email sign up
  const handleEmailSignup = (e) => {
    e.preventDefault();
    const { name, email, password } = formData;

    // If fields are empty, throw error
    if (name === "" || email === "" || password === "") {
      ErrorNotification("Please fill in all fields");
      return;
    }

    // A function to check if the name container email or website
    const checkName = (name) => {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
      const websiteRegex =
        /((http|https):\/\/)?(www.)?[a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-zA-Z0-9()]{2,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
      return emailRegex.test(name) || websiteRegex.test(name);
    };

    // Make sure the name field doesn't contain any numbers, html, script tags and should not be longer than 40 characters
    if (
      name.length > 40 ||
      name.includes("<") ||
      name.includes(">") ||
      name.includes("script")
    ) {
      ErrorNotification("Nice try hacker ;)");
      return;
    }

    // Password length should not be more than 40 characters
    if (password.length > 40) {
      ErrorNotification("Password should not be more than 40 characters");
      return;
    }

    // Make sure the name field doesn't contain email or website
    if (checkName(name)) {
      ErrorNotification("Name cannot contain email or website");
      return;
    }

    // If not agreed, show error
    if (!agree) {
      ErrorNotification("You need to agree to the terms and conditions");
      return;
    }

    // If captcha not verified, show error
    if (!captchaSuccess) {
      ErrorNotification("Please verify that you are not a robot");
      return;
    }

    try {
      // Dispatch name, email, and password
      dispatch(
        signupAction({
          name,
          email,
          password,
          history,
          setFormData,
          initialFormData,
          setAgree,
          setLoading,
        })
      );
    } catch (error) {
      console.log("SIGNUP_ERROR", error);
    }
  };

  // Handle google login success
  const handleGoogleSuccess = async (credentialResponse) => {
    const userObject = jwt_decode(credentialResponse.credential);
    const { name, email } = userObject;
    setLoading(true);

    // Dispatch email, password and history to action
    dispatch(googleLoginAction({ name, email, history, setLoading }));
  };

  // Handle google login failure
  const handleGoogleFailure = () => {
    console.log("GOOGLE_SIGNUP_ERROR");
  };

  // Handle recaptcha change
  const handleRecaptchaChange = (value) => {
    verifyCaptcha(value)
      .then((res) => {
        if (res.data.success) {
          setCaptchaSuccess(true);
        } else {
          setCaptchaSuccess(false);
        }
      })
      .catch((err) => {
        console.log("RECAPTCHA_ERROR", err);
        setCaptchaSuccess(false);
      });
  };

  return (
    <>
      <Helmet>
        <title>Sign Up | Copilotly - Your Personal AI Copilot</title>
        <meta name="description" content="Create your Copilotly account. Get AI-powered copilots for Legal, Medical, Financial, Marketing, Code, and Writing. Works on any webpage." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app.copilotly.com/signup" />
        <meta property="og:title" content="Sign Up | Copilotly - Your Personal AI Copilot" />
        <meta property="og:description" content="Create your Copilotly account. Get AI-powered copilots for any task." />
        <meta property="og:url" content="https://app.copilotly.com/signup" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Row
        gutter={[32, 0]}
        className="hp-authentication-page"
        style={{ overflowY: "auto", height: "100vh" }}
      >
        <LeftContent />

        <Col
          lg={8}
          md={16}
          sm={16}
          span={24}
          className="hp-py-sm-0 hp-py-md-0"
          justify="center"
        >
          <Row className="hp-h-100" align="middle" justify="center">
            <Col
              xxl={11}
              xl={15}
              lg={20}
              md={16}
              sm={24}
              className="hp-px-sm-8"
              style={{ paddingTop: "12px", paddingBottom: "16px" }}
            >
              <img
                src={logoText}
                alt="logo"
                style={{
                  maxWidth: "140px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto 8px",
                }}
              />
              <h2
                className="hp-mb-sm-0"
                style={{
                  textAlign: "center",
                  fontSize: "18px",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.85)",
                  marginBottom: "12px",
                }}
              >
                Create Account
              </h2>

              <Form layout="vertical" name="basic" style={{ fontSize: "13px" }}>
                <Form.Item
                  style={{ marginBottom: "10px" }}
                  rules={[
                    {
                      required: true,
                      message: "Please input your name!",
                    },
                  ]}
                >
                  <Input
                    id="name"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleFormChange}
                    style={{ height: "36px", fontSize: "13px" }}
                  />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
                  rules={[
                    { required: true, message: "Please enter your email" },
                  ]}
                >
                  <Input
                    id="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleFormChange}
                    style={{ height: "36px", fontSize: "13px" }}
                  />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
                  rules={[
                    { required: true, message: "Please enter your password" },
                  ]}
                >
                  <Input.Password
                    id="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleFormChange}
                    style={{ height: "36px", fontSize: "13px" }}
                  />
                </Form.Item>

                {/* Check box agreeing to terms and privacy */}
                <Form.Item style={{ marginBottom: "8px" }}>
                  <Checkbox
                    checked={agree}
                    onChange={() => setAgree(!agree)}
                    style={{ fontSize: "12px" }}
                  >
                    <span style={{ fontSize: "12px" }}>
                      I agree to the{" "}
                      <Link
                        to="/terms"
                        className="hp-text-color-blue-100 hp-text-color-dark-80"
                      >
                        Terms
                      </Link>
                      ,{" "}
                      <Link
                        to="/privacy"
                        className="hp-text-color-blue-100 hp-text-color-dark-80"
                      >
                        Privacy
                      </Link>{" "}
                      &{" "}
                      <Link
                        to="/ethics"
                        className="hp-text-color-blue-100 hp-text-color-dark-80"
                      >
                        Ethics
                      </Link>
                    </span>
                  </Checkbox>
                </Form.Item>

                {/* Recaptcha check */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: "scale(0.85)",
                    transformOrigin: "center",
                    marginBottom: "-8px",
                  }}
                >
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    size="normal"
                  />
                </div>

                <Form.Item style={{ marginTop: "12px", marginBottom: "8px" }}>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    onClick={handleEmailSignup}
                    loading={loading}
                    style={{ height: "36px", fontSize: "13px" }}
                  >
                    Sign up
                  </Button>
                </Form.Item>
              </Form>

              <div className="login-create-acc" style={{ marginBottom: "6px", fontSize: "13px" }}>
                <p style={{ margin: 0 }}>Already have an account?</p>
                <Link to="/login" style={{ marginLeft: "4px" }}> Login</Link>
              </div>

              <div className="divider" style={{ margin: "8px 0", fontSize: "12px" }}>Or</div>

              <div
                className="google-login-button"
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <GoogleLogin
                  onSuccess={(credentialResponse) => {
                    handleGoogleSuccess(credentialResponse);
                  }}
                  onError={() => {
                    handleGoogleFailure();
                  }}
                />
              </div>

              <Col
                className="hp-other-links"
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: "12px",
                }}
              >
                <Link
                  to="/privacy"
                  className="hp-text-color-black-80 hp-text-color-dark-40"
                  style={{
                    marginRight: "8px",
                    fontSize: "11px",
                  }}
                >
                  Privacy Policy
                </Link>

                <span
                  className="hp-text-color-black-80 hp-text-color-dark-40"
                  style={{ margin: "0 6px", fontSize: "11px" }}
                >
                  ·
                </span>

                <Link
                  to="/terms"
                  className="hp-text-color-black-80 hp-text-color-dark-40"
                  style={{ fontSize: "11px" }}
                >
                  Terms of Service
                </Link>
              </Col>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Signup;
